import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { History, Menu } from 'lucide-react';
import './App.css';
import './ActivityLogPage.css';
import AppSidebar from './components/AppSidebar';
import NotificationBell from './components/NotificationBell';
import { logout as endSession } from './auth';

const API_ROOT = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_URL || '')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5001');
const API_BASE_URL = API_ROOT ? `${API_ROOT}/api` : '/api';

const ACTION_LABELS = {
  client_created: 'Client created',
  client_updated: 'Client updated',
  client_deleted: 'Client deleted',
  clients_bulk_imported: 'Bulk client upload',
  drawing_uploaded: 'Drawing uploaded',
  drawing_reviewed: 'Drawing reviewed'
};

const DETAIL_LABELS = {
  remark: 'Remark',
  client_id: 'Client ID',
  attach_name: 'Drawing',
  approval_status: 'Approval',
  assigned_architect: 'Architect',
  previous_architect: 'Previous architect',
  status: 'Status',
  source: 'Source',
  spreadsheet_row: 'Spreadsheet row',
  imported: 'Imported',
  skipped: 'Skipped'
};

function formatWhen(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function parseDetails(details) {
  if (!details) return [];
  const obj = typeof details === 'string'
    ? (() => {
      try { return JSON.parse(details); } catch { return null; }
    })()
    : details;
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj)
    .filter(([, v]) => v != null && v !== '')
    .map(([key, value]) => ({
      key,
      label: DETAIL_LABELS[key] || key.replace(/_/g, ' '),
      value: typeof value === 'object' ? JSON.stringify(value) : String(value)
    }));
}

function DetailsCell({ details }) {
  const items = parseDetails(details);
  if (!items.length) {
    return <span className="activity-details-empty">—</span>;
  }
  return (
    <div className="activity-details">
      {items.map((item) => (
        <span key={item.key} className="activity-detail-chip">
          <strong>{item.label}:</strong>
          <span>{item.value}</span>
        </span>
      ))}
    </div>
  );
}

export default function ActivityLogPage() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actor, setActor] = useState('');
  const [action, setAction] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/activity`, {
        params: {
          page,
          limit: 25,
          actor: actor || undefined,
          action: action || undefined
        }
      });
      setRows(data.data || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [page, actor, action]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="App activity-page">
      <ToastContainer theme="light" position="top-right" autoClose={3000} />
      <div className="app-shell">
        <AppSidebar
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          active="activity"
          onLogout={() => endSession()}
        />
        <div className="app-main">
          <header className="app-topbar">
            <div className="app-topbar-actions">
              <button
                type="button"
                className="btn btn-outline sidebar-toggle"
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
              >
                <Menu size={18} />
                Menu
              </button>
              <h1 className="app-topbar-title">Activity log</h1>
            </div>
            <div className="app-topbar-actions">
              <NotificationBell />
            </div>
          </header>

          <div className="container">
            <div className="card activity-card">
              <header className="activity-head">
                <History size={20} />
                <div>
                  <h2>Audit trail</h2>
                  <p>{total} event{total === 1 ? '' : 's'} recorded</p>
                </div>
              </header>

              <div className="activity-filters">
                <input
                  className="form-control"
                  placeholder="Filter by actor username"
                  value={actor}
                  onChange={(e) => {
                    setPage(1);
                    setActor(e.target.value);
                  }}
                />
                <select
                  className="form-control"
                  value={action}
                  onChange={(e) => {
                    setPage(1);
                    setAction(e.target.value);
                  }}
                >
                  <option value="">All actions</option>
                  {Object.entries(ACTION_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              {loading ? (
                <p className="activity-loading">Loading…</p>
              ) : rows.length === 0 ? (
                <p className="activity-empty">No activity yet.</p>
              ) : (
                <div className="activity-table-wrap">
                  <table className="table activity-table">
                    <thead>
                      <tr>
                        <th className="col-when">When</th>
                        <th className="col-actor">Actor</th>
                        <th className="col-action">Action</th>
                        <th className="col-client">Client</th>
                        <th className="col-details">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.id}>
                          <td className="activity-when">{formatWhen(row.created_at)}</td>
                          <td>
                            <span className="activity-actor-name">{row.actor_username}</span>
                            <div className="activity-role">{row.actor_role || '—'}</div>
                          </td>
                          <td>
                            <span className="activity-action">
                              {ACTION_LABELS[row.action] || row.action}
                            </span>
                          </td>
                          <td>
                            {row.client_id ? (
                              <Link className="activity-client-link" to={`/clients/${row.client_id}`}>
                                #{row.client_id}
                              </Link>
                            ) : '—'}
                          </td>
                          <td>
                            <DetailsCell details={row.details} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="pagination-bar">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
